import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlan } from '../../contexts/PlanContext';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import UpgradePrompt from '../../components/UpgradePrompt';
import { useToast } from '../../components/ui/use-toast';

const Branding = () => {
  const { t } = useTranslation('billing');
  const { canUse } = usePlan();
  const { toast } = useToast();
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#1e3a5f');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const enabled = canUse('custom_branding');

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        setLoading(true);
        const res = await api.get<any>('/branding');
        const data = (res as any).data || res;
        setCompanyName(data.branding_company_name || '');
        setPrimaryColor(data.branding_primary_color || '#1e3a5f');
        setLogoUrl(data.branding_logo_url || null);
      } catch (err) {
        console.error('Failed to fetch branding:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/branding', {
        branding_company_name: companyName,
        branding_primary_color: primaryColor,
      });
      toast({ title: t('branding_saved') });
    } catch (err: any) {
      toast({ title: err.message || 'Error', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    try {
      setUploading(true);
      const res = await api.post<any>('/branding/logo', formData);
      const data = (res as any).data || res;
      setLogoUrl(data.branding_logo_url || null);
      toast({ title: t('branding_saved') });
    } catch (err: any) {
      toast({ title: err.message || 'Error', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent" />
      </div>
    );
  }

  if (!enabled) {
    return <UpgradePrompt feature="custom_branding" />;
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h3 className="font-semibold mb-1">{t('branding_title')}</h3>
        <p className="text-sm text-slate-500">{t('branding_desc')}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t('company_name')}</Label>
          <Input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Corp"
          />
        </div>

        <div className="space-y-2">
          <Label>{t('primary_color')}</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-10 w-14 rounded border cursor-pointer"
            />
            <Input
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-32 font-mono"
              maxLength={7}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('logo')}</Label>
          {logoUrl && (
            <div className="mb-2">
              <img
                src={logoUrl}
                alt="Company logo"
                className="max-h-20 max-w-[200px] rounded border object-contain bg-white p-1"
              />
            </div>
          )}
          <Input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={handleLogoUpload}
            disabled={uploading}
          />
          <p className="text-xs text-slate-400">{t('logo_hint')}</p>
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? '...' : t('save_branding')}
        </Button>
      </div>
    </div>
  );
};

export default Branding;
